import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { getAppCustomerId } from "../../../App";
import {
  attemptShowErrorsInSnackbar,
  showSnackbarType,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { Card, MenuButton } from "@velocity-ehs/component-library";
import { Input } from "../../sharedComponents/inputs/Input";
import Select from "../../sharedComponents/inputs/Select";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import fieldTrim from "../../supportFunctions/FieldTrim";
import {
  createSSOIncomingMutation,
  deleteSSOIncomingMutation,
  updateSSOIncomingMutation,
} from "../../supportFunctions/graphql/SSOIncomingQueries";
import {
  defaultMetaData,
  Metadata,
  JIT_OPTIONS,
  MetadataFormValues,
} from "./Metadata";
import { FileUpload } from "primereact/fileupload";
import classes from "./MetadataForm.module.scss";
import classNames from "classnames/bind";
import FormSkeleton from "../../sharedComponents/FormSkeleton";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import AuthService from "./services/AuthService";

export enum Mode {
  Add,
  Edit,
  View,
}

export interface Props {
  mode: Mode;
  metaData?: Metadata;
  onSaveChanges?: (() => Promise<void>) | (() => void);
  isLoading?: boolean;
  formMethods?: UseFormReturn<MetadataFormValues, unknown>;
}

const MetadataForm: React.FC<Props> = ({
  mode = Mode.View,
  metaData = defaultMetaData,
  onSaveChanges,
  isLoading = false,
  formMethods,
}) => {
  const cx = classNames.bind(classes);
  let history = useHistory();
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const [waitingUpload, setWaitingUpload] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const modificationMode: Mode = mode;
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = formMethods;

  useEffect(() => {
    if (metaData) {
      reset({
        ...metaData,
        jitEnabled: JIT_OPTIONS.find(
          (option) => metaData?.jitEnabled === option.value
        ),
      });
    }
    // eslint-disable-next-line
  }, [metaData, isLoading]);

  const onSubmit = useCallback(
    (formValues: MetadataFormValues) => {
      // trim all fields on metadata form data
      const trimmedFormValues = fieldTrim({ formData: formValues });
      const data = {
        entityId: trimmedFormValues.entityId,
        x509Cert: trimmedFormValues.x509Cert,
        ssoURL: trimmedFormValues.ssoURL,
        ssoLogOutURL: trimmedFormValues.ssoLogOutURL,
        ssoLogOutBinding: trimmedFormValues.ssoLogOutBinding,
        name: UsersService.customer.name,
        customerid:
          modificationMode === Mode.Edit
            ? metaData?.customerid
            : UsersService.customer.id,
        customer: {
          id:
            modificationMode === Mode.Edit
              ? metaData?.customerid
              : UsersService.customer.id,
        },
        jitEnabled: formValues.jitEnabled.value,
      } as Metadata;

      if (modificationMode === Mode.Edit || modificationMode === Mode.View) {
        return updateMetadata({
          metadata: data,
          props: { mode, metaData, onSaveChanges, isLoading, formMethods },
          showSnackbar: showSnackbar,
          t: t,
        });
      } else {
        return addMetadata({
          metadata: data,
          showSnackbar: showSnackbar,
          props: { mode, metaData, onSaveChanges, isLoading, formMethods },
          t: t,
        });
      }
    },
    // eslint-disable-next-line
    [metaData]
  );

  const onMetadataFile = useCallback(
    (e: any) => {
      const selectedFile = e.target.files[0];
      uploadFile(selectedFile);
    },
    // eslint-disable-next-line
    []
  );

  const uploadFile = useCallback(
    (file: any) => {
      const data = new FormData();
      if (file) {
        data.append("file", file);

        setWaitingUpload(true);
        AuthService.api()
          .post(`/saml/idpmetadata/${getAppCustomerId()}`, data)
          .then((response) => {
            setWaitingUpload(false);

            if (response.data) {
              let responseData = response.data;
              responseData.customerid = getAppCustomerId();
              responseData.name = UsersService.customer.name;
              reset(responseData);
            } else {
              showSnackbar(
                SnackbarType.Error,
                t("sso_failed_to_parse_error_message")
              );
            }
          })
          .catch((e) => {
            showSnackbar(
              SnackbarType.Error,
              t("sso_failed_to_parse_error_message")
            );
            setWaitingUpload(false);
          });
      }
    },
    // eslint-disable-next-line
    [reset]
  );

  const actionButtons = () => {
    if (!metaData) return [];

    const items = [
      {
        label: t("universal_Upload"),
        template: () => {
          return (
            <FileUpload
              mode="basic"
              chooseOptions={{
                label: t("universal_Upload"),
                className: cx(
                  "p-button-text p-button-secondary",
                  classes.uploadButton
                ),
              }}
              name="ssoFile"
              accept="image/xml"
              onUpload={onMetadataFile}
            />
          );
        },
        // This will be hidden until Upload functionality is added later.
        show: false,
      },
      {
        label: t("universal_Delete"),
        command: () =>
          deleteEntity({
            metadata: metaData,
            history: history,
            showSnackbar: showSnackbar,
            setIsDeleting: setIsDeleting,
            t: t,
          }),
        show: modificationMode === Mode.Edit,
        className: "warning",
      },
    ];

    return items.filter((item) => item.show);
  };

  const headerAction = () => {
    if (actionButtons().length === 0) return <></>;
    return (
      <MenuButton
        label={t("universal_Actions")}
        items={actionButtons()}
        icon="pi-chevron-down"
        buttonClass="p-button-text"
      />
    );
  };

  const card = (
    <Card title={t("universal_Details")} headerAction={headerAction()}>
      {isLoading ? (
        <FormSkeleton rows={5} />
      ) : (
        <form id="singleSignOnForm" onSubmit={handleSubmit(onSubmit)}>
          {/* @ts-ignore */}
          <Input
            control={control}
            label={t("singleSignOn_Entity_ID")}
            name="entityId"
            rules={{ required: t("required_message_entity_id") }}
            errorMessage={errors && errors.entityId?.message}
            disabled={
              modificationMode === Mode.View || modificationMode === Mode.Edit
            }
          />
          <Input
            control={control}
            label={t("singleSignOn_X509Cert")}
            name="x509Cert"
            rules={{ required: t("required_message_x509_cert") }}
            errorMessage={errors && errors.x509Cert?.message}
            disabled={modificationMode === Mode.View}
          />
          <Input
            control={control}
            label={t("singleSignOn_SSO_URL")}
            name="ssoURL"
            disabled={modificationMode === Mode.View}
            rules={{ required: t("required_message_sso_url") }}
            errorMessage={errors && errors.ssoURL?.message}
          />
          <Input
            control={control}
            label={t("singleSignOn_SSO_Logout_URL")}
            name="ssoLogOutURL"
            disabled={modificationMode === Mode.View}
          />
          <Input
            control={control}
            label={t("singleSignOn_SSO_Logout_Binding")}
            name="ssoLogOutBinding"
            disabled={modificationMode === Mode.View}
          />
          <Select
            control={control}
            options={JIT_OPTIONS}
            label={t("sso_jitProvisioning")}
            name="jitEnabled"
            display="label"
            value={JIT_OPTIONS.find(
              (option) => metaData?.jitEnabled === option.value
            )}
            disabled={modificationMode === Mode.View}
          />
        </form>
      )}
    </Card>
  );

  return card;
};

export type AddMetadataProps = {
  metadata: Metadata;
  showSnackbar: showSnackbarType | undefined;
  props: Props;
  t: any;
};
export const addMetadata = async ({
  metadata,
  props,
  showSnackbar,
  t,
}: AddMetadataProps) => {
  try {
    await AuthService.graphql().post("", createSSOIncomingMutation(metadata));
    showSnackbar(SnackbarType.Success, t("sso_successfully_added_message"));
    props.onSaveChanges();
  } catch (error) {
    attemptShowErrorsInSnackbar(showSnackbar, error);
  }
};

export type UpdateMetadataProps = {
  metadata: Metadata;
  props: Props;
  showSnackbar: showSnackbarType | undefined;
  t: any;
};
export const updateMetadata = async ({
  metadata,
  props,
  showSnackbar,
  t,
}: UpdateMetadataProps) => {
  if (props.metaData) {
    try {
      await AuthService.graphql().post(
        "",
        updateSSOIncomingMutation(props.metaData.id, metadata)
      );
      showSnackbar(
        SnackbarType.Success,
        t("sso_successfully_updated_message", {
          entityId: metadata.entityId,
        })
      );
      props.onSaveChanges();
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  }
};

export type DeleteEntityProps = {
  metadata: Metadata | undefined;
  showSnackbar: showSnackbarType | undefined;
  setIsDeleting: (value: boolean) => void;
  history: any;
  t: any;
};
export const deleteEntity = async ({
  metadata,
  showSnackbar,
  setIsDeleting,
  history,
  t,
}: DeleteEntityProps) => {
  if (metadata) {
    try {
      setIsDeleting(true);
      await AuthService.graphql().post(
        "",
        deleteSSOIncomingMutation(metadata.id)
      );
      showSnackbar(
        SnackbarType.Success,
        t("sso_successfully_deactivated_message", {
          entityId: metadata.entityId,
        })
      );
      history.goBack();
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsDeleting(false);
    }
  }
};

export default MetadataForm;
